/** Server-side speech recognition on the client: which engine to use and how to record for it.
 *
 *  Decision 0015: the backend's `GET /voice/config` says which engine a
 *  client should start with (`browser` = Web Speech, `server` = record a
 *  clip and POST it to `/voice`).  `?stt=server|browser` on the page
 *  overrides that for the tab (remembered in sessionStorage; `?stt=auto`
 *  forgets it), which is how the owner tests the server path on the phone
 *  before the default flips.  Everything here is pure; Chat.tsx and
 *  useVoiceInput do the fetching and recording. */

export type SttEngine = "browser" | "server";

/** What `GET /voice/config` returns. */
export interface VoiceConfig {
  engine: SttEngine;
  /** A provider is configured and the budget is not exhausted (for the override). */
  server_available: boolean;
  max_seconds: number;
  max_bytes: number;
}

/** The engine for this tab: an override wins when the server can honour it; otherwise the server's choice.
 *  An unknown config (old backend, maintenance, network error) means the browser engine. */
export function resolveEngine(config: VoiceConfig | null, override: SttEngine | null): SttEngine {
  if (override === "browser") return "browser";
  if (!config) return "browser";
  if (override === "server") return config.server_available ? "server" : "browser";
  return config.engine === "server" && config.server_available ? "server" : "browser";
}

export const STT_OVERRIDE_KEY = "scoutdex_stt_override";

interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** The `?stt=` override for this tab, after recording a new one from the URL in `store`.
 *  `?stt=server` / `?stt=browser` are remembered, `?stt=auto` clears, anything else is ignored.
 *  Storage failures (private mode) leave the URL value in force for this call only. */
export function sttOverride(search: string, store: StringStore | null): SttEngine | null {
  const fromUrl = new URLSearchParams(search).get("stt");
  if (fromUrl === "server" || fromUrl === "browser") {
    try { store?.setItem(STT_OVERRIDE_KEY, fromUrl); } catch { /* storage blocked */ }
    return fromUrl;
  }
  if (fromUrl === "auto") {
    try { store?.removeItem(STT_OVERRIDE_KEY); } catch { /* storage blocked */ }
    return null;
  }
  try {
    const stored = store?.getItem(STT_OVERRIDE_KEY);
    return stored === "server" || stored === "browser" ? stored : null;
  } catch {
    return null;
  }
}

/** The server path stops a clip here; 512 KB covers 15 s in any codec MediaRecorder produces. */
export const MAX_SERVER_RECORDING_MS = 15_000;

// In preference order: iOS Safari only records audio/mp4; Chrome and Edge
// prefer opus in webm; Firefox records ogg.
const RECORDING_MIMES = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/ogg"];

/** The first recording type the browser supports, or null when MediaRecorder can't say. */
export function pickRecordingMime(isTypeSupported: ((mime: string) => boolean) | undefined): string | null {
  if (!isTypeSupported) return null;
  for (const mime of RECORDING_MIMES) {
    try {
      if (isTypeSupported(mime)) return mime;
    } catch { /* an old MediaRecorder may throw on unknown types */ }
  }
  return null;
}

/** A file name whose extension matches the clip's type (the backend reads the type, the name is for logs). */
export function fileNameFor(mime: string): string {
  const bare = mime.split(";")[0].trim().toLowerCase();
  const ext = bare === "audio/mp4" ? "m4a" : bare === "audio/ogg" ? "ogg" : bare === "audio/wav" ? "wav" : "webm";
  return `clip.${ext}`;
}
